import { Translation } from 'cng-common/features/translations'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { IColumn } from 'core-components/library/list/types'
import { Tooltip } from 'core-components/library/tooltip'
import { Tag } from 'partner360/components/qualityIndicator'
import { FINANCIAL_ROUNDING_CONFIG } from 'partner360/configs/financial/financialFormatting'
import { getAutoRounding } from 'partner360/helpers/getAutoRounding'
import { buildJbpPlanActionUrl } from 'partner360/lib/jbpPlanActionFlow'

import { TRANSLATION_KEYS } from '../../../../../configs/translations'
import {
  IFinancialPlanQualityCategoryScore,
  TFinancialPlanQualityValueUnit,
} from '../../../../../model/types'
import styles from '../FinancialPlanQualityPillar.module.scss'

const NO_DATA_PLACEHOLDER = '-'

export interface IFinancialPlanQualityTableRow extends IFinancialPlanQualityCategoryScore {
  parentId: string
  hasChildren: boolean
}

const getPlanningUrl = ({
  partnerId,
  fYear,
  programType,
  category,
}: NonNullable<IFinancialPlanQualityCategoryScore['planningLink']>): string =>
  buildJbpPlanActionUrl({
    partnerId,
    fyear: fYear,
    offering: programType,
    category,
  })

const renderCategoryName = (
  item: IFinancialPlanQualityTableRow,
  onGoToPlanning: (url: string) => void
) => {
  const label = <Translation text={item.category} span />

  if (!item.planningLink) {
    return label
  }

  const planningUrl = getPlanningUrl(item.planningLink)

  return (
    <Tooltip
      tipContent={() => <Translation text={TRANSLATION_KEYS.quality.actions.goToPlanning} span />}
      inline
    >
      <button
        type="button"
        className={styles.categoryLink}
        onClick={() => onGoToPlanning(planningUrl)}
      >
        {label}
      </button>
    </Tooltip>
  )
}

const formatValue = (value: number | null, valueUnit: TFinancialPlanQualityValueUnit) => {
  if (value === null) {
    return NO_DATA_PLACEHOLDER
  }

  const sign = value > 0 ? '+' : ''
  const suffix = valueUnit === 'percent' ? '%' : ' pp'

  return `${sign}${value.toFixed(1)}${suffix}`
}

const formatPlannedValue = (
  planned: number | null,
  absoluteChange: number | null,
  valueUnit: TFinancialPlanQualityValueUnit
): string => {
  const formattedPlanned = formatValue(planned, valueUnit)

  if (planned === null || absoluteChange === null || valueUnit !== 'percent') {
    return formattedPlanned
  }

  const rounding = getAutoRounding(absoluteChange)
  const { divisor, fractionDigits, suffix } = FINANCIAL_ROUNDING_CONFIG[rounding]
  const sign = absoluteChange > 0 ? '+' : ''
  const formattedAbsoluteChange = getFormattedFinance(absoluteChange / divisor, {
    withCurrencySign: false,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  return `${formattedPlanned} (${sign}${formattedAbsoluteChange}${suffix})`
}

export const getColumns = (
  valueUnit: TFinancialPlanQualityValueUnit,
  onGoToPlanning: (url: string) => void
): IColumn<IFinancialPlanQualityTableRow>[] => [
  {
    field: 'category',
    title: TRANSLATION_KEYS.quality.details.category,
    width: 320,
    titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.category} />,
    renderFunction: (item, _column, rowAttrs) => {
      const depth = rowAttrs?.deep ?? 0

      return (
        <span className={styles.categoryLabel}>
          <span className={styles.categoryText} style={{ marginLeft: depth * 16 }}>
            {renderCategoryName(item, onGoToPlanning)}
          </span>
        </span>
      )
    },
  },
  {
    field: 'planned',
    title: TRANSLATION_KEYS.quality.details.plannedYoY,
    className: styles.centeredColumn,
    titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.plannedYoY} />,
    renderFunction: (item) => formatPlannedValue(item.planned, item.absoluteChange, valueUnit),
  },
  {
    field: 'target',
    title: TRANSLATION_KEYS.quality.details.marketTarget,
    className: styles.centeredColumn,
    titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.marketTarget} />,
    renderFunction: (item) => formatValue(item.target, valueUnit),
  },
  {
    field: 'points',
    title: TRANSLATION_KEYS.quality.details.points,
    className: styles.centeredColumn,
    titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.points} />,
    renderFunction: (item) => (item.points === null ? NO_DATA_PLACEHOLDER : <b>{item.points}</b>),
  },
  {
    field: 'band',
    title: TRANSLATION_KEYS.quality.details.band,
    className: styles.centeredColumn,
    titleRender: () => <Translation text={TRANSLATION_KEYS.quality.details.band} />,
    renderFunction: (item) => (item.band ? <Tag variant={item.band} /> : NO_DATA_PLACEHOLDER),
    hideSettings: true,
    disableSort: true,
  },
]
